import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import _ from "lodash";
import {
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Tooltip,
  Button,
} from "@mui/material";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import ConfirmDialog from "../ConfirmDialog";
import { updateDataTableSettings } from "../../actions";
import TrayFetchFilter from "./TrayFetchFilter";
import {
  buttonStyles,
  TableContainerStyle,
  CircularProgressStyles,
  TableHeadStyle,
  TableStyle,
} from "./DataTableStyle";
import { caseInsensitiveIteree } from "../../utils/helpers";

const ACTIONS_COLUMN = {
  field: "actions",
  label: "Actions",
  isSortable: false,
  align: "right",
};

const getField = (row, field) =>
  field.split(".").reduce((ret, subfield) => ret?.[subfield] || null, row);

const DataTable = ({
  dataList,
  total,
  tableName,
  columns,
  isFetchingData,
  onClickRow,
  onClickEdit,
  handleAddData,
  handleDeleteData,
  onContextMenu,
  options,
  actions = [],
  page = 0,
  pageSize = 50,
  orderBy,
  order,
  loading,
  rowsPerPageOptions,
  updateDataTableSettings,
}) => {
  const {
    allowCreate = false,
    allowClick = false,
    allowContext = false,
  } = options;

  const [showConfirmation, setShowConfirmation] = useState({});
  const [data, setData] = useState([]);

  useEffect(() => {
    setData(
      _.chain(dataList)
        .orderBy([caseInsensitiveIteree(orderBy)], [order])
        .slice(page * pageSize, Math.min(total, (page + 1) * pageSize))
        .value()
    );
  }, [dataList, orderBy, order, total, page, pageSize]);

  if (actions.length > 0) {
    columns = [...columns, ACTIONS_COLUMN];
  }

  const handleChangePage = (e, newPage) => {
    updateDataTableSettings(tableName, { page: newPage });
  };

  const handleChangeRowsPerPage = (e) => {
    updateDataTableSettings(tableName, { page: 0, pageSize: e.target.value });
  };

  const handleSorting = (property) => (e) => {
    const isAsc = property === orderBy && order === "asc";
    updateDataTableSettings(tableName, {
      order: isAsc ? "desc" : "asc",
      orderBy: property,
    });
  };

  return (
    <Paper className={"flex flex-col"}>
      <div className={"flex flex-row justify-between"}>
        {(page || page === 0) && pageSize && handleChangePage ? (
          <TablePagination
            rowsPerPageOptions={rowsPerPageOptions}
            component={"div"}
            rowsPerPage={pageSize}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            count={total}
            backIconButtonProps={{
              sx: { ...buttonStyles.infoButton },
            }}
            nextIconButtonProps={{
              sx: { ...buttonStyles.infoButton },
            }}
            sx={TableContainerStyle}
          />
        ) : null}

        {loading ? (
          <CircularProgress sx={CircularProgressStyles} />
        ) : tableName === "Collection Site" && allowCreate ? (
          <Tooltip
            title={`Add new ${tableName}`}
            sx={{
              float: "right",
            }}
          >
            <Button
              onClick={handleAddData}
              sx={{
                ...buttonStyles.infoButton,
                fontSize: [
                  "0.6rem",
                  "0.6rem",
                  "0.6rem",
                  "0.6rem",
                  "0.875rem",
                  "0.875rem",
                ],
                padding: 0,
              }}
            >
              Create
            </Button>
          </Tooltip>
        ) : (
          <TrayFetchFilter tableName={tableName} />
        )}
      </div>
      <TableContainer
        style={{
          height: "75vh",
          width: "100%",
        }}
      >
        <Table stickyHeader sx={TableStyle}>
          <TableHead sx={TableHeadStyle}>
            <TableRow sx={buttonStyles.tableHeadRow}>
              {columns.map((column) => {
                return (
                  <TableCell
                    key={column.field}
                    align={column.align}
                    sx={{ backgroundColor: "#fafafa", ...column?.sx }}
                  >
                    {column.isSortable ? (
                      <TableSortLabel
                        active={orderBy === column.field}
                        direction={orderBy === column.field ? order : "asc"}
                        onClick={handleSorting(column.field)}
                      >
                        {column.label}
                      </TableSortLabel>
                    ) : (
                      column.label
                    )}
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((row, index) => {
              return (
                <TableRow
                  hover
                  className={allowClick ? "cursor-pointer" : "cursor-default"}
                  onClick={allowClick ? () => onClickRow(row) : () => null}
                  onContextMenu={
                    allowContext ? (e) => onContextMenu(e, row) : () => null
                  }
                  key={`table-row-${row.id}-${index}`}
                >
                  {columns
                    .slice(0, actions.length > 0 ? -1 : undefined)
                    .map((col) => (
                      <TableCell
                        align={col.align}
                        key={`table-cell-row${row.id}-col${col.field}`}
                        sx={{
                          ...(col?.sx || {}),
                          color: col?.heightLightColor
                            ? col.heightLightColor(getField(row, col.field))
                            : "default",
                        }}
                      >
                        <Typography
                          noWrap
                          sx={{
                            fontSize: { xs: "0.6rem", lg: "0.875rem" },
                          }}
                        >
                          {(col?.formatFn || ((s) => s))(
                            getField(row, col.field)
                          )}
                        </Typography>
                      </TableCell>
                    ))}
                  {actions.length > 0 ? (
                    <TableCell padding={"none"} align="right">
                      {actions.map((action) =>
                        action ? (
                          <span key={`action-${action.name}`}>
                            {action?.dialog ? (
                              <ConfirmDialog
                                title={`${action.name} ${tableName}`}
                                open={
                                  Array.isArray(
                                    showConfirmation?.[action.name]
                                  ) &&
                                  showConfirmation?.[action.name].length > 0
                                }
                                setOpen={() =>
                                  setShowConfirmation({
                                    ...showConfirmation,
                                    [action.name]: false,
                                  })
                                }
                                onConfirm={showConfirmation?.[action.name]?.[1]}
                              >
                                {showConfirmation?.[action.name]?.[0]}
                              </ConfirmDialog>
                            ) : null}
                            <Tooltip title={`${action.name} ${tableName}`}>
                              <IconButton
                                onClick={() =>
                                  action?.dialog
                                    ? setShowConfirmation({
                                        ...showConfirmation,
                                        [action.name]: [
                                          action.dialog(row),
                                          () => action.handler(row),
                                        ],
                                      })
                                    : action.handler(row)
                                }
                                sx={{
                                  ...action.sx,
                                  padding: { xs: "0.5rem", lg: ".75rem" },
                                }}
                              >
                                <action.icon
                                  sx={{
                                    fontSize: [
                                      "1.25rem",
                                      "1.25rem",
                                      "1.25rem !important",
                                      "1.25rem !important",
                                      "1.5rem !important",
                                      "1.5rem !important",
                                    ],
                                  }}
                                />
                              </IconButton>
                            </Tooltip>
                          </span>
                        ) : null
                      )}
                    </TableCell>
                  ) : null}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

export const mapStateToProps = (state, { tableName }) => {
  if (tableName) {
    return state?.settings?.[tableName] || {};
  }
  return {};
};

export default connect(mapStateToProps, { updateDataTableSettings })(DataTable);
